/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { get_array_of_strings_from_ml_text, str_trim } from './util.js';
import { jQuery } from '../other/jquery-hooked.js';
import { mjt } from './i18ntexts.js';
import { MDLocale } from './locale-impl.js';
import { MDMeta } from './meta-data.js';


/**
 * Manja Metadata Filter Widgets ( .mjMetaFilterWidget )
 *
 * common widget options:
 *  - meta_id	: int
 *  - meta_type	: int (enum MetaType, MT_*)
 *  - type		: mj_mfw_type
 *  - change	: function( value )
 *  - init_done	: function( )
 *  - config	: type-specific configuration
 *
 * types:
 *  - string				single-line text
 *  	html:				input type=text
 *  	config:
 *  		+ op			for meta_type string: e, m, ne, z; for meta_type int or real: e, ne, g, ge, l, le, z
 *
 *  - list					list of values
 *  	html:				select or radio buttons
 *  	config:
 *  		+ values		[ [value,label], [value,label], ... ]
 *  						- where value is a list of op+cmp_value tuples: [[op,v],[op,v],...]
 *
 *  - val_list				like list, but with a simplified configuration
 *  	html:				select
 *  	config:
 *  		+ values		string
 *  						- where each line represents a single value
 *  						- the values will be submitted as an "eq" operation
 *
 *  - auto_list				like list, but available values will be determined automatically from available content
 *  - auto2_list			like list, but available values will be determined from metadata field configuration (vl_defaults & vl_translations)
 *
 *  - set					multiple choice
 *  	html:				list of checkboxes
 *  	config:
 *  		+ op			eo (equal to one of), mo (match one of)
 *  		+ values		[ [value,label], [value,label], ... ]
 *
 *  - val_set				liste set, but with a simplified configuration
 *  	html:				list of checkboxes
 *  	config:
 *  		+ op			eo (equal to one of), mo (match one of)
 *  		+ values		string
 *  						- where each line represents a single value
 *
 *  - auto_set				like set, but available values will be determined automatically from available content
 *  - auto2_set				like set, but available values will be determined from metadata field configuration (vl_defaults & vl_translations)
 *  - auto3_set				like set, but available values are not proposed as a list, but are available as tags (after typing first few letter)
 *
 *  - date					date
 *  	html:				jquery ui datepicker
 *  	config:
 *  		+ op			e, ne, g, ge, l, le, z
 *
 *  - date_range			date range
 *  	html:				two jquery ui datepickers
 *  	config:
 *  		-none-
 *
 * methods:
 *  - init(opts)
 *  - get()			: return current filter value
 *  - getPostData()	: return object with form elements names as keys and filter data values
 *  - set(val)		: set new filter value
 *  - empty()		: return whether value is empty/unset/inactive
 *  - reset()		: reset to an empty value
 *  - format(value)	: return formatted value (html for display)
 *  - options()		: return options that were used on init()
 *  - config()		: alias for options()
 *
 * usage:
 *  $('#container').mjMetaFilterWidget( {...} )
 *  var is_empty = $('#container').mjMetaFilterWidget('empty');
 *  $('#container').mjMetaFilterWidget('reset')
 *
 */
(function($) {

	const meta_filter_widget_base = {
		get: function( opts ) {
			if( opts.init_in_progress ) return null;
			return $(this).mjWidget('get');
		},
		getPostData: function( opts ) {
			if( opts.init_in_progress ) return {};
			const name = $(this).mjWidget('name');
			let postData = {};
			postData[name] = opts._widget_methods.get.call(this,opts);
			return postData;
		},
		set: function( opts, val, safe ) {
			if( opts.init_in_progress ) return;
			return $(this).mjWidget('set',val,safe);
		},
		empty: function( opts ) {
			if( opts.init_in_progress ) return true;
			const r0 = $(this).mjWidget('get');
			if( Array.isArray(r0) ) return r0.length===0;
			return r0===null || r0==='';
		},
		reset: function( opts, triggerChange ) {
			if( opts.init_in_progress ) return;
			$(this).mjWidget('set','');
			if( triggerChange ) {
				// if( opts.change ) opts.change.call($(this),triggerChange);
				$(this).mjWidget('trigger');
			}
		},
		meta_id: function( opts ) {
			return opts.meta_id;
		},
		format: function( opts, val ) {
			return val;
		},
		is_init_in_progress: function( opts ) {
			return opts.init_in_progress || false;
		},
	};

	let meta_filter_widgets = {
		'string': Object.assign( {}, meta_filter_widget_base, {
			init: function( opts ) {
				$(this).mjWidget( {
					type: 'string',
					name: 'search[flt:'+opts.meta_id+':'+opts.config.op+']',
					change: opts.change,
				} );
			},
		} ),
		'list': Object.assign( {}, meta_filter_widget_base, {
			init: function( opts ) {
				// first entry is an empty item
				var nv = [ ['',''] ];
				// json stringify values (for inclusion into <option value=".."> tags)
				var v;
				const vl = opts.config.values.length;
				for( var i=0; i<vl; ++i ) {
					v = opts.config.values[i];
					const v0str = JSON.stringify(v[0]);
					nv.push( [ v0str, ...v.slice(1) ] );
				}
				let mjw_opts = {
					type: 'list',
					name: 'search[flt:'+opts.meta_id+']' + ( opts.multiple ? '[]' : '' ),
					change: opts.change,
					values: nv,
					value: opts.hasOwnProperty('value') ? opts.value : '',
				};
				if( opts.multiple ) mjw_opts.multiple = true;
				if( opts.dropdownParent ) mjw_opts.dropdownParent = opts.dropdownParent;

				$(this)
					.mjWidget( mjw_opts )
					.find('.select2-selection__rendered').addClass('no-tooltip');
			},
			format: function( opts, val ) {
				const vl = opts.config.values.length;
				for( var i=0; i<vl; ++i ) {
					const v = opts.config.values[i];
					const vk = Array.isArray(v[0]) ? JSON.stringify(v[0]) : v[0];
					if( val==vk ) return v[1];
				}
				return null;
			},
		} ),
		'set': Object.assign( {}, meta_filter_widget_base, {
			init: function( opts ) {
				$(this).mjWidget( {
					type: 'set',
					name: 'search[flt:'+opts.meta_id+':'+opts.config.op+']',
					change: opts.change,
					values: opts.config.values,
				} );
			},
			reset: function( opts, triggerChange ) {
				if( opts.init_in_progress ) return;
				$(this).mjWidget('set',[]);
				if( triggerChange ) {
					// if( opts.change ) opts.change.call($(this),triggerChange);
					$(this).mjWidget('trigger');
				}
			},
			format: function( opts, val ) {
				var vls = Array.isArray(val) ? val : [val],
					vl = opts.config.values.length,
					nvls = vls.map( function( val, val_idx ) {
						for( var i=0; i<vl; ++i ) {
							var v = opts.config.values[i];
							if( val==v[0] ) return v[1];
						}
						return val;
					} );
				return nvls.join(' '+mjt('search-info-or')+' ');
			},
		} ),
		'date': Object.assign( {}, meta_filter_widget_base, {
			init: function( opts ) {
				$(this).mjWidget( {
					type: 'date',
					name: 'search[flt:'+opts.meta_id+':'+opts.config.op+']',
					change: opts.change,
				} );
			},
			format: function( opts, val ) {
				return MDLocale.fmt_date(val);
			},
		} ),
		'date_range': Object.assign( {}, meta_filter_widget_base, {
			init: function( opts ) {
				var onchange = null;
				if( typeof opts.change === 'function' ) {
					const self = this;
					onchange = function() {
						opts.change.call(self);
					};
				}
				let parent = $('<div class="date"></div>');
				let parent_from = $('<div class="date-wrapper"></div>');
				let parent_to = $('<div class="date-wrapper"></div>');

				let from = $('<div class="date-fields date-widget-from"></div>').mjWidget( {
					type: 'date',
					name: 'search[flt:'+opts.meta_id+':ge]',
					change: onchange,
					placeholder: opts.placeholder_from
				} );
				let to = $('<div class="date-fields date-widget-to"></div>').mjWidget( {
					type: 'date',
					name: 'search[flt:'+opts.meta_id+':le]',
					change: onchange,
					placeholder: opts.placeholder_to
				} );
				let label_from = $('<label/>', { 'class': 'line', 'for':from.mjWidget('id') } ).text(mjt('search-options-datetime-from','!'));
				let label_to = $('<label/>', { 'class': 'line', 'for':to.mjWidget('id') } ).text(mjt('search-options-datetime-to','!'));
				parent_from.append(label_from).append(from);
				parent_to.append(label_to).append(to);
				parent.append(parent_from).append(parent_to).appendTo(this);
			},
			get: function( opts ) {
				if( opts.init_in_progress ) return null;
				const wdg_elems = $(this).find('.date-fields');
				return {
					from: wdg_elems.eq(0).mjWidget('get'),
					to:   wdg_elems.eq(1).mjWidget('get'),
				};
			},
			getPostData: function( opts ) {
				if( opts.init_in_progress ) return null;
				const wdg_elems = $(this).find('.date-fields');
				let postData = {};
				const nameFrom = wdg_elems.eq(0).mjWidget('name');
				postData[nameFrom] = wdg_elems.eq(0).mjWidget('get');
				const nameTo = wdg_elems.eq(1).mjWidget('name');
				postData[nameTo] = wdg_elems.eq(1).mjWidget('get');
				return postData;
			},
			set: function( opts, value, safe ) {
				if( opts.init_in_progress ) return;
				const wdg_elems = $(this).find('.date-fields');
				wdg_elems.eq(0).mjWidget( 'set', value.hasOwnProperty('from') && value.from ? value.from : '', safe );
				wdg_elems.eq(1).mjWidget( 'set', value.hasOwnProperty('to') && value.to ? value.to : '', safe );
			},
			empty: function( opts ) {
				if( opts.init_in_progress ) return;
				const wdg_elems = $(this).find('.date-fields');
				return wdg_elems.eq(0).mjWidget('get')=='' && wdg_elems.eq(1).mjWidget('get')=='';
			},
			reset: function( opts, triggerChange ) {
				if( opts.init_in_progress ) return;
				$(this).find('.date-fields').mjWidget('set','');
				if( triggerChange ) {
					// if( opts.change ) opts.change.call($(this),triggerChange);
					$(this).children('span.col-2').mjWidget('trigger');
				}
			},
			format: function( opts, val ) {
				var r = '';
				if( val.hasOwnProperty('from') && val.from!='' ) r += mjt('search-info-datetime-from')+' '+MDLocale.fmt_date(val.from);
				if( val.hasOwnProperty('to') && val.to!='' ) r = str_trim(r+' '+mjt('search-info-datetime-to')+' '+MDLocale.fmt_date(val.to));
				return r;
			},
		} ),
	};


	meta_filter_widgets.val_list = Object.assign( {}, meta_filter_widgets.list, {
		init: function( opts ) {
			const self = this;
			const xvals = opts.config.values.split('\n');
			let values = [];
			for( var i=0; i<xvals.length; ++i ) {
				const val = xvals[i];
				if( str_trim(val)!='' ) {
					values.push( [ [ ['e',val] ], val ] );
				}
			}
			opts.config = {values:values};
			meta_filter_widgets.list.init.call(self,opts);
		},
	} );

	meta_filter_widgets.val_set = Object.assign( {}, meta_filter_widgets.set, {
		init: function( opts ) {
			const self = this;
			let values = [];
			const xvals = opts.config.values.split('\n');
			for( var i=0; i<xvals.length; ++i ) {
				const val = xvals[i];
				if( str_trim(val)!='' ) {
					values.push( [val,val] );
				}
			}
			opts.config = {op:opts.config.op||'eo',values:values};
			meta_filter_widgets.set.init.call(self,opts);
		},
	} );


	function getMFConfigValue( val, mfwType, ddr, addCssClass ) {
		const lbl = ddr.getDisplayDataItem(val,true);
		const optVal = mfwType==='list' ? [ ['e',val] ] : val;
		if( addCssClass ) return [optVal,lbl,false,null,false,addCssClass];
		return [optVal,lbl];
	}

	function buildMFConfigFromMetaDataValueList( meta_id, dataValueList, mfwType ) {
		const ddr = MDMeta.getDisplayDataRenderer(meta_id);
		const len = dataValueList.length;
		let configValues = new Array(len);
		for( var i=0; i<len; ++i ) {
			configValues[i] = getMFConfigValue(dataValueList[i],mfwType,ddr);
		}
		if( mfwType==='list' ) {
			return { values: configValues };
		}
		return { op:'eo', values: configValues };
	}

	function areDataDependenciesFullyInitialized( opts ) {
		for( const [,count] of Object.entries(opts.waitingForDataDependenciesInitialized??{}) ) {
			if( count<1 ) return false;
		}
		return true;
	}

	function getMergedWaitingForDataDependenciesStates( opts ) {
		let mergedStates = {};
		for( const [,ddStates] of Object.entries(opts.waitingForDataDependenciesStates??{}) ) {
			const actualDDStates = Object.fromEntries( Object.entries(ddStates).filter( ([key,value]) => value!=='' ) );
			Object.assign(mergedStates,actualDDStates);
		}
		return mergedStates;

	}

	// Cache with key -> dataValueList,
	// where key is composed from:
	// - 1. optCfgLIdx
	// - 2. meta_id
	// - 3. mfwType
	// - 4. filterOpts, excluding the options with no effect on result (the empty ones)
	let m_MFDeferedDataCacheAndState = {};

	function createMFDeferedDataCacheKey( opts, filterOpts ) {
		//const actualFilterOpts = Object.fromEntries( Object.entries(filterOpts).filter( ([key,value]) => value!=='' ) );
		const actualFilterOptsArr = Object.entries(filterOpts).filter( ([key,value]) => value!=='' );
		actualFilterOptsArr.sort( (a,b) => a[0].localeCompare(b[0]) );
		const cacheKeyObj = {
			i: opts.optCfgLIdx,
			m: opts.meta_id,
			t: opts.mfwType,
			o: actualFilterOptsArr,
		};
		const cacheKeyStr = JSON.stringify(cacheKeyObj);
		return cacheKeyStr;
	}
	function queryMFDeferedDataCache( key ) {
		if( m_MFDeferedDataCacheAndState.hasOwnProperty(key) ) {
			return m_MFDeferedDataCacheAndState[key];
		}
		return null;
	}
	function writeMFDeferedDataCache( key, val ) {
		return m_MFDeferedDataCacheAndState[key] = val;
	}



	const meta_filter_defered_mixin = {
		initDeferedMixin( opts, mfwType, queryDataFn, defaultFilterOpts ) {
			const self = this;
			opts.init_in_progress = true;
			opts.mfwType = mfwType;
			opts.queryDataFn = queryDataFn;
			opts.curLoadedDoneDCKey = null;
			opts.curDCKey = null;

			const filterOpts = defaultFilterOpts || {};
			const dcKey = createMFDeferedDataCacheKey(opts,filterOpts);
			return meta_filter_defered_mixin.loadAndCacheFilteredData.call(self,opts,true,filterOpts,dcKey);
		},


		startLoadFilteredData: function( opts, isInitialUpdate, filterOpts, dcKey, dcState ) {
			opts.curLoadedDoneDCKey = null;
			opts.curDCKey = dcKey;
			if( opts.load_filtered_data_start ) {
				opts.load_filtered_data_start.call(this,opts,isInitialUpdate,filterOpts,dcKey,dcState);
			}
		},
		doneLoadFilteredData: function( opts, isInitialUpdate, filterOpts, dcKey, dcState, dataValueList ) {
			if( opts.load_filtered_data_done ) {
				opts.load_filtered_data_done.call(this,opts,isInitialUpdate,filterOpts,dcKey,dcState,dataValueList);
			}
			if( dcKey===opts.curDCKey ) {
				opts.curDCKey = null;
			}
			opts.curLoadedDoneDCKey = dcKey;
		},

		updateFromFilteredData: function( opts, isInitialUpdate, dataValueList ) {
			// remember the currently selected value(s):
			const dataValueListStr = JSON.stringify(dataValueList);
			if( isInitialUpdate || !( (opts.curDataValueListStr??null)===dataValueListStr ) ) {
				let prevValue = null;
				if( !isInitialUpdate ) {
					prevValue = isInitialUpdate ? null : meta_filter_widgets[opts.mfwType].get.call(this,opts);
					// TODO: avoid full destroy and init -> use .mjWidget('replace') when possible(?)
					$(this).mjWidget('destroy');
				}

				opts.curDataValueListStr = dataValueListStr;
				opts.config = buildMFConfigFromMetaDataValueList(opts.meta_id,dataValueList,opts.mfwType);
				meta_filter_widgets[opts.mfwType].init.call(this,opts);

				if( !isInitialUpdate ) {
					if( prevValue!==null && prevValue!=='' && !(Array.isArray(prevValue)&&prevValue.length===0) ) {
						// if a previously selected value exists ...
						// try to SET it again on the widget ...
						meta_filter_widgets[opts.mfwType].set.call(this,opts,prevValue,true);
						// ... and GET it back from widget
						const curValue = meta_filter_widgets[opts.mfwType].get.call(this,opts);
						if( curValue===null || curValue==='' || (Array.isArray(curValue)&&curValue.length===0) ) {
							// .. if it could NOT be SET, then reset() with trigger ...
							meta_filter_widgets[opts.mfwType].reset.call(this,opts,true);
						}
					} else {
						// else: a previously selected valued does not exist ...
						meta_filter_widgets[opts.mfwType].set.call(this,opts,'');
					}
				}
			}
			if( isInitialUpdate ) {
				if( opts.hasOwnProperty('init_in_progress') ) delete opts.init_in_progress;
				if( opts._defered_init_resolve ) opts._defered_init_resolve();
			}
		},

		loadAndCacheFilteredData( opts, isInitialUpdate, filterOpts, dcKey ) {
			const self = this;
			let dcState = writeMFDeferedDataCache( dcKey, {
				dcKey,
				queryStartTime: Date.now(),
				isInitialUpdate,
				curRequest: null,
				cacheTime: null,
				cachedDataValueList: null,
			} );

			meta_filter_defered_mixin.startLoadFilteredData.call(this,opts,isInitialUpdate,filterOpts,dcKey,dcState);

			// start querying
			return dcState.curRequest = opts.queryDataFn( opts, filterOpts, (dataValueList) => {
				// update cache states on response ..
				let dcState = queryMFDeferedDataCache(dcKey);
				dcState.queryStartTime = null;
				dcState.curRequest = null;
				dcState.cacheTime = Date.now();
				dcState.cachedDataValueList = dataValueList;

				if( dcState.isInitialUpdate || dcKey===opts.curDCKey ) {
					// .. and update actual widget
					meta_filter_defered_mixin.updateFromFilteredData.call(self,opts,dcState.isInitialUpdate,dataValueList);
					meta_filter_defered_mixin.doneLoadFilteredData.call(this,opts,dcState.isInitialUpdate,filterOpts,dcKey,dcState,dataValueList);
				} else {
					// console.log({w:'SKIP updFromFilteredData', metaId:opts.meta_id, iiu:dcState.isInitialUpdate,  dcKey,  curDCKey:opts.curDCKey,  dataValueList });
				}
			} );
		},

		loadFilteredData( opts, filterOpts, extraLogData ) {
			const dcKey = createMFDeferedDataCacheKey(opts,filterOpts);
			let dcState = queryMFDeferedDataCache(dcKey);

			// console.log({ w:'loadFilteredData', metaId:opts.meta_id, iiu:false,  dcKey,  filterOpts,  ...extraLogData,  dcState });

			if( dcState!==null ) {
				if( dcState.queryStartTime != null ) {
					const msSinceQueryStarted = Date.now() - dcState.queryStartTime;
					if( msSinceQueryStarted < 15*1000 ) {
						// in progress, .. -> wait!
						return;
					}
					// in progress, but waited for too long..
					// maybe abort the old request (by dcState.curRequest) ?
				} else if( dcState.cacheTime ) {
					const msSinceCacheTime = Date.now() - dcState.cacheTime;
					if( msSinceCacheTime < 10*60*1000 ) {
						// cached & fresh
						// console.log({ w:'msSinceCacheTime', metaId:opts.meta_id, iiu:false,  dcKey,  msSinceCacheTime   });
						if( opts.curLoadedDoneDCKey !== dcKey ) {
							meta_filter_defered_mixin.startLoadFilteredData.call(this,opts,false,filterOpts,dcKey,dcState);
							meta_filter_defered_mixin.updateFromFilteredData.call(this,opts,false,dcState.cachedDataValueList);
							meta_filter_defered_mixin.doneLoadFilteredData.call(this,opts,false,filterOpts,dcKey,dcState,dcState.cachedDataValueList);
						} else {
							// no update required
						}
						return;
					}
					// in progress, but waited for too long...
				}
			} else {
				// no cache -> start querying
			}
			return meta_filter_defered_mixin.loadAndCacheFilteredData.call(this,opts,false,filterOpts,dcKey);
		},

		updateDependentFilterOption( opts, initiatingOptCfgLIdx, filterOptionPostData, initiatingOptCfg ) {
			if( !opts.hasOwnProperty('waitingForDataDependenciesInitialized') ) return;
			opts.waitingForDataDependenciesInitialized[initiatingOptCfgLIdx] ++;
			opts.waitingForDataDependenciesStates[initiatingOptCfgLIdx] = filterOptionPostData;
			if( areDataDependenciesFullyInitialized(opts) ) {
				const filterOpts = getMergedWaitingForDataDependenciesStates(opts);
				meta_filter_defered_mixin.loadFilteredData.call(this,opts,filterOpts,{initiatingOptCfgLIdx,filterOptionPostData,initiatingOptCfg});
			}
		},
	};


	const dataQueryFn_on_MetaDefsDefaults = ( opts, filterOpts, onData ) => {
		MDMeta.queryDefinition( opts.meta_id, function( md ) {
			const dataValueList = md.hasOwnProperty('options') && md.options.hasOwnProperty('vl_defaults') ? get_array_of_strings_from_ml_text(md.options.vl_defaults) : [];
			onData(dataValueList);
		} );
		return null;
	};

	const dataQueryFn_on_LiveMetaDataValues = ( opts, filterOpts, onData ) => {
		const postData = {
			meta_id: opts.meta_id,
			...filterOpts
		};
		return $.postJSON( 'json/widgets/meta-values_list', postData, function(j) {
			const dataValueList = j.values;
			onData(dataValueList);
		} );
	};



	meta_filter_widgets.auto2_list = Object.assign( {}, meta_filter_widgets.list, meta_filter_defered_mixin, {
		init: function( opts, defaultFilterOpts ) {
			const self = this;
			meta_filter_defered_mixin.initDeferedMixin.call( self, opts, 'list', dataQueryFn_on_MetaDefsDefaults, defaultFilterOpts );
		},
	} );

	meta_filter_widgets.auto2_set = Object.assign( {}, meta_filter_widgets.set, meta_filter_defered_mixin, {
		init: function( opts, defaultFilterOpts ) {
			const self = this;
			meta_filter_defered_mixin.initDeferedMixin.call( self, opts, 'set', dataQueryFn_on_MetaDefsDefaults, defaultFilterOpts );
		},
	} );




	meta_filter_widgets.auto_list = Object.assign( {}, meta_filter_widgets.list, meta_filter_defered_mixin, {
		init: function( opts, defaultFilterOpts ) {
			const self = this;
			meta_filter_defered_mixin.initDeferedMixin.call( self, opts, 'list', dataQueryFn_on_LiveMetaDataValues, defaultFilterOpts );
		},
	} );

	meta_filter_widgets.auto_set = Object.assign( {}, meta_filter_widgets.set, meta_filter_defered_mixin, {
		init: function( opts, defaultFilterOpts ) {
			const self = this;
			meta_filter_defered_mixin.initDeferedMixin.call( self, opts, 'set', dataQueryFn_on_LiveMetaDataValues, defaultFilterOpts );
		},
	} );

	meta_filter_widgets.auto3_set = Object.assign( {}, meta_filter_widgets.list, meta_filter_defered_mixin, {
		init: function( opts, defaultFilterOpts ) {
			const self = this;
			// additional options on meta filter widget 'list':
			opts.multiple = true;
			opts.ignore_case = true;
			meta_filter_defered_mixin.initDeferedMixin.call( self, opts, 'list', dataQueryFn_on_LiveMetaDataValues, defaultFilterOpts );
		},
		format: function( opts, val ) {
			const vls = Array.isArray(val) ? val : [val];
			const vl = opts.config.values.length;
			const nvls = vls.map( function( xval, val_idx ) {
				if( typeof xval === 'string' && xval.length>2 && xval[0]=='[' && xval[1]=='[' ) xval = JSON.parse(xval);
				if( Array.isArray(xval) && xval.length>=1 ) xval = xval[0];
				if( Array.isArray(xval) && xval.length>1 ) xval = xval[1];
				for( var i=0; i<vl; ++i ) {
					const v = opts.config.values[i];
					if( xval==v[0] ) return v[1];
				}
				return xval;
			} );
			return nvls.join(' '+mjt('search-info-or')+' ');
		},
	} );



	function _define_meta_filter_widget_methods( self, opts ) {
		if( !meta_filter_widgets[opts.type] ) throw new Error('Unknown mjMetaFilterWidget type "'+opts.type+'" (while initializing it)');
		opts._widget_methods = meta_filter_widgets[opts.type];
	}


	const methods = {
		init: function( opts ) {
			if( !opts.type ) throw new Error('No mjMetaFilterWidget type specified');
			_define_meta_filter_widget_methods(this,opts);
			const self = $(this);
			self.data('mj-mfw-opts',opts);

			const remove_promise = function() {
				// // necessary at all?
				// if( opts.hasOwnProperty('_defered_init_reject') ) delete opts._defered_init_reject;
				// if( opts.hasOwnProperty('_defered_init_resolve') ) delete opts._defered_init_resolve;
				// delete opts._defered_init_promise;
			};

			if( opts.hasOwnProperty('waitForDataDependenciesArr') && opts.waitForDataDependenciesArr.length ) {

				opts.waitingForDataDependenciesInitialized = {};
				opts.waitingForDataDependenciesStates = {};
				for( const waitForOptCfgLIdx of opts.waitForDataDependenciesArr ) {
					opts.waitingForDataDependenciesInitialized[waitForOptCfgLIdx] = 0;
					// opts.waitingForDataDependenciesStates[waitForOptCfgLIdx] = null;
				}
			}

			opts._defered_init_promise = new Promise( (resolve,reject) => {
				opts._defered_init_resolve = resolve;
				opts._defered_init_reject = reject;
			} ).then( () => {
				remove_promise();
				if( opts.init_done ) opts.init_done(opts);
			} ).catch( (err) => {
				remove_promise();
				// console.log({w:'mjMetaFilterWidget defered init failed',opts});
			} );

			const init_args = Array.prototype.slice.call(arguments);
			opts._widget_methods.init.apply(this,init_args);
			if( opts._widget_methods.is_init_in_progress.call(this,opts) ) {
				// console.log({w:'mjMetaFilterWidget init defered',opts});
			} else {
				// console.log({w:'mjMetaFilterWidget init normal',opts});
				remove_promise();
			}
		},
		destroy: function( opts ) {
			//naaah!?//if( opts && opts._defered_init_reject ) opts._defered_init_reject();
			$(this).empty().removeData('mj-mfw-opts');
		},
		options: function( opts ) {
			return opts;
		},
		config: function( opts ) {
			return opts;
		},
		get: 'pass',
		getPostData: 'pass',
		set: 'pass',
		empty: 'pass',
		reset: 'pass',
		format: 'pass',
		is_init_in_progress: 'pass',

		loadFilteredData: 'pass',
		updateDependentFilterOption: 'pass',
	};


	$.fn.mjMetaFilterWidget = function mjMetaFilterWidget( arg ) {
		// -> "this" is a jQuery object
		var args = arguments;
		if( typeof arg === 'object' || !arg ) {
			this.each( function mjMetaFilterWidget1_init() {
				// -> "this" is a DOMElement
				methods.init.apply(this,args);
			} );
			return this;
		} else if( methods[arg] ) {
			const method = methods[arg];
			const item_args = Array.prototype.slice.call(args);
			let rv = undefined;
			this.each( function mjMetaFilterWidget1_other() {
				// -> "this" is a DOMElement
				item_args[0] = $(this).data('mj-mfw-opts');
				if( item_args[0]===undefined && arg!=='destroy' ) {
					throw new Error('Element was not (yet) initialized as an mjMetaFilterWidget');
				}
				rv = ( method==='pass' ? item_args[0]._widget_methods[arg] : method ).apply(this,item_args);
			} );
			return rv===undefined ? this : rv;
		}
		throw new Error('Method '+arg+' does not exist on jQuery.mjMetaFilterWidget');
	};
})(jQuery);
